/**
 * System alertów dla zamówień
 * Pozwala na tworzenie customowych reguł warunkowych do podświetlania rekordów
 */

export type AlertColor = 'red' | 'orange';

export type AlertOperator =
  | 'isEmpty'           // pole jest puste (null/undefined)
  | 'isNotEmpty'        // pole ma wartość
  | 'equals'            // pole == wartość
  | 'notEquals'         // pole != wartość
  | 'before'            // data < wartość (data w przeszłości)
  | 'after'             // data > wartość (data w przyszłości)
  | 'beforeToday'       // data < dzisiaj
  | 'afterToday'        // data > dzisiaj
  | 'isToday'           // data == dzisiaj
  | 'daysAgo'           // ile dni temu (data < dzisiaj - X dni)
  | 'daysFromNow'       // za ile dni (data < dzisiaj + X dni)
  | 'olderThan'         // różnica > X dni (np. akceptacja była, ale produkcja nie wystartowała przez >X dni)
  | 'fieldComparison';  // porównanie dwóch pól (np. proposed_date < confirmed_date)

export interface AlertCondition {
  field: string;                    // nazwa pola z OrderRecord (np. 'proposed_lead_time_collection')
  operator: AlertOperator;
  value?: string | number | null;   // wartość do porównania (dla niektórych operatorów)
  compareToField?: string;          // drugie pole (dla fieldComparison)
  description?: string;             // opis warunku (opcjonalny, dla UI)
}

export interface AlertRule {
  id: string;
  name: string;                     // nazwa reguły (np. "Proponowany załadunek przeszedł")
  description?: string;             // dłuższy opis reguły
  color: AlertColor;                // kolor podświetlenia
  priority: number;                 // priorytet (100 = critical, 50 = warning, 10 = info)
  enabled: boolean;
  conditions: AlertCondition[];     // wszystkie warunki muszą być spełnione (AND logic)
  conditionLogic?: 'AND' | 'OR';   // logika łączenia warunków (domyślnie AND)
}

export interface AlertResult {
  ruleId: string;
  ruleName: string;
  color: AlertColor;
  priority: number;
  matchedConditions: number;
  totalConditions: number;
}

// Predefiniowane reguły (przykłady)
export const DEFAULT_ALERT_RULES: AlertRule[] = [
  {
    id: 'critical-1',
    name: 'Proponowany załadunek przeszedł',
    description: 'Proponowany termin załadunku minął, a brak potwierdzonego załadunku',
    color: 'red',
    priority: 100,
    enabled: true,
    conditions: [
      {
        field: 'proposed_lead_time_collection',
        operator: 'beforeToday',
        description: 'Data proponowanego załadunku < dzisiaj'
      },
      {
        field: 'confirmed_collection_date',
        operator: 'isEmpty',
        description: 'Brak potwierdzonego załadunku'
      }
    ]
  },
  {
    id: 'critical-2',
    name: 'Potwierdzony załadunek dzisiaj - brak produkcji',
    description: 'Potwierdzony załadunek jest dzisiaj, a zamówienie nie jest w produkcji',
    color: 'red',
    priority: 95,
    enabled: true,
    conditions: [
      {
        field: 'confirmed_collection_date',
        operator: 'isToday',
        description: 'Potwierdzony załadunek dzisiaj'
      },
      {
        field: 'in_production_since',
        operator: 'isEmpty',
        description: 'Brak daty rozpoczęcia produkcji'
      }
    ]
  },
  {
    id: 'critical-3',
    name: 'Szacowana dostawa przeszła',
    description: 'Szacowana data dostawy minęła, a brak realnej dostawy',
    color: 'red',
    priority: 90,
    enabled: true,
    conditions: [
      {
        field: 'estimated_delivery_date',
        operator: 'beforeToday',
        description: 'Szacowana dostawa < dzisiaj'
      },
      {
        field: 'real_delivery_date',
        operator: 'isEmpty',
        description: 'Brak realnej daty dostawy'
      }
    ]
  },
  {
    id: 'warning-1',
    name: 'Akceptacja rysunków - brak produkcji >1 dzień',
    description: 'Rysunki zaakceptowane, ale produkcja nie wystartowała przez >1 dzień',
    color: 'orange',
    priority: 50,
    enabled: true,
    conditions: [
      {
        field: 'drawing_approval_date',
        operator: 'isNotEmpty',
        description: 'Akceptacja rysunków istnieje'
      },
      {
        field: 'drawing_approval_date',
        operator: 'daysAgo',
        value: 1,
        description: 'Akceptacja była >1 dzień temu'
      },
      {
        field: 'in_production_since',
        operator: 'isEmpty',
        description: 'Brak daty produkcji'
      }
    ]
  },
  {
    id: 'warning-2',
    name: 'Plan rysunków przeszedł - brak akceptacji',
    description: 'Planowana data rysunków minęła, a brak akceptacji',
    color: 'orange',
    priority: 45,
    enabled: true,
    conditions: [
      {
        field: 'drawings_planned_date',
        operator: 'beforeToday',
        description: 'Plan rysunków < dzisiaj'
      },
      {
        field: 'drawing_approval_date',
        operator: 'isEmpty',
        description: 'Brak akceptacji rysunków'
      }
    ]
  },
  {
    id: 'warning-3',
    name: 'Dostawa za mniej niż 3 dni - brak potwierdzenia',
    description: 'Szacowana dostawa za <3 dni, a brak potwierdzonego załadunku',
    color: 'orange',
    priority: 40,
    enabled: true,
    conditions: [
      {
        field: 'estimated_delivery_date',
        operator: 'daysFromNow',
        value: 3,
        description: 'Dostawa za <3 dni'
      },
      {
        field: 'confirmed_collection_date',
        operator: 'isEmpty',
        description: 'Brak potwierdzonego załadunku'
      }
    ]
  }
];

// Kolory i ich mapowanie na klasy Tailwind
export const ALERT_COLOR_CONFIG: Record<AlertColor, {
  bg: string;
  border: string;
  text: string;
  label: string;
  icon: string;
}> = {
  red: {
    bg: 'bg-red-50',
    border: 'border-l-4 border-red-500',
    text: 'text-red-900',
    label: 'KRYTYCZNY',
    icon: '🔴'
  },
  orange: {
    bg: 'bg-orange-50',
    border: 'border-l-4 border-orange-500',
    text: 'text-orange-900',
    label: 'UWAGA',
    icon: '🟠'
  }
};
